import { ConcreteFormStepProps } from './interfaces'
import { useTranslation } from 'react-i18next'
import { NextStep } from '../components/NextStep'
import * as yup from 'yup'
import { useStepForm } from './hooks'
import { Controller, useFormState } from 'react-hook-form'
import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material'
import React from 'react'
import { getCompanyCountryCodeFor } from './utilities'
import * as ibantools from 'ibantools'
import * as ibantoolsGermany from 'ibantools-germany'
import { IMaskMixin } from 'react-imask'
import { bankDataByIBAN } from 'bankdata-germany'
import Grid from '@mui/material/Unstable_Grid2'
import { CountrySelect } from '../components/CountrySelect'
import { TextFieldProps } from '@mui/material/TextField/TextField'

ibantools.setCountryBBANValidation('DE', ibantoolsGermany.isValidBBAN);

const MaskedInput = IMaskMixin(({ inputRef, ...props }: TextFieldProps) => (
    <TextField
        {...props}
        fullWidth={true}
        inputRef={inputRef}
    />
))

export const BankAccount = ({ values, onSubmit, lead }: ConcreteFormStepProps) => {
    const { t } = useTranslation()
    const [hasBankData, setHasBankData] = React.useState(false)

    const schema = yup.object({
        iban: yup.string().test({
            name: 'valid-iban',
            test (value, ctx) {
                return noIban || value !== undefined && ibantools.isValidIBAN(value.replace(/\s/g, ''))
            }
        }),
        bic: yup.string().test({
            name: 'valid-bic',
            test (value, ctx) {
                return noIban || value !== undefined && ibantools.isValidBIC(value.replace(/\s/g, ''))
            }
        }),
        bankName: yup.string().required(),
        accountHolder: yup.string().required(),
        accountNumber: yup.string().when('noIban', {
            is: true,
            then: (schema) => schema.required().min(3)
        }),
        bankCode: yup.string().when('noIban', {
            is: true,
            then: (schema) => schema.required().min(3)
        }),
        bankLocality: yup.string().when('noIban', {
            is: true,
            then: (schema) => schema.required().min(3)
        }),
        bankCountry: yup.string().when('noIban', {
            is: true,
            then: (schema) => schema.required()
        }),
    })

    const {
        control,
        handleSubmit,
        augmentedFieldProps,
        watch,
        setValue
    } = useStepForm('BankAccount', schema, values, false)
    const {
        errors
    } = useFormState({ control })

    const companyCountryCode = getCompanyCountryCodeFor(lead)
    const isCompanySepaCountry = ibantools.isSEPACountry(companyCountryCode)
    const noIban = isCompanySepaCountry ? false : (watch('noIban') ?? false)
    const iban = watch('iban')?.replace(/\s/g, '')

    React.useEffect(() => {
        if (isCompanySepaCountry) {
            setValue('noIban', false)
        }
    }, [isCompanySepaCountry])

    React.useEffect(() => {
        if (noIban) {
            setHasBankData(false)
            return
        }
        const bankData = bankDataByIBAN(iban)
        if (bankData) {
            setValue('bankName', bankData.bankName)
            setValue('bic', bankData.bic)
            setHasBankData(true)
        } else {
            setHasBankData(false)
        }
    }, [iban, noIban])

    const onSubmitWithDataTransform = (data: Record<string, any>) => {
        if (data.noIban) {
            onSubmit({
                ...data,
                iban: '',
                bic: '',
            })
        } else {

            onSubmit({
                ...data,
                iban: data.iban.replace(/\s/g, ''),
                accountNumber: '',
                bankCode: '',
                bankCountry: data.iban.substring(0, 2),
                bankLocality: '',
            })
        }
    }

    return <>
        <Typography mb={4} variant="h5" component="h1">{t('BankAccount.heading')}</Typography>

        <form onSubmit={handleSubmit(onSubmitWithDataTransform)}>
            <Stack spacing={3} maxWidth={500}>
                <Box>
                    <Grid container columnSpacing={2} sx={noIban ? {} : { display: 'none' }}>
                        <Grid xs={7}>
                            <Controller
                                name="accountNumber"
                                control={control}
                                defaultValue=""
                                render={({ field }) => <TextField
                                    {...augmentedFieldProps(field)}
                                    fullWidth={true}
                                />}
                            />
                        </Grid>
                        <Grid xs={5}>
                            <Controller
                                name="bankCode"
                                control={control}
                                defaultValue=""
                                render={({ field }) => <TextField
                                    {...augmentedFieldProps(field)}
                                    fullWidth={true}
                                />}
                            />
                        </Grid>
                    </Grid>
                    <Grid container columnSpacing={2} sx={noIban ? { display: 'none' } : {}}>
                        <Grid xs={7}>
                            <Controller
                                name="iban"
                                control={control}
                                defaultValue=""
                                render={({ field }) => <TextField
                                    {...augmentedFieldProps(field)}
                                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                    fullWidth={true}
                                />}
                            />
                        </Grid>
                        <Grid xs={5}>
                            <Controller
                                name="bic"
                                control={control}
                                defaultValue=""
                                render={({ field }) => <TextField
                                    {...augmentedFieldProps(field)}
                                    onChange={(e) => field.onChange(e.target.value.toUpperCase().replace(/\s/g, ''))}
                                    disabled={hasBankData}
                                    fullWidth={true}
                                />}
                            />
                        </Grid>
                    </Grid>
                    <Controller
                        name="noIban"
                        control={control}
                        render={({ field }) =>
                            <FormControlLabel
                                label={augmentedFieldProps(field).label}
                                sx={isCompanySepaCountry ? { display: 'none' } : {}}
                                control={
                                    <Checkbox
                                        {...field}
                                        checked={noIban}
                                    />
                                }
                            />
                        }
                    />
                </Box>

                <Controller
                    name="bankName"
                    control={control}
                    defaultValue=""
                    render={({ field }) => <TextField
                        {...augmentedFieldProps(field)}
                        disabled={hasBankData}
                        fullWidth={true}
                    />}
                />

                <Box sx={noIban ? {} : { display: 'none' }}>
                    <Grid container columnSpacing={2}>
                        <Grid xs={6}>
                            <Controller
                                name="bankLocality"
                                control={control}
                                defaultValue=""
                                render={({ field }) => <TextField
                                    {...augmentedFieldProps(field)}
                                    fullWidth={true}
                                />}
                            />
                        </Grid>
                        <Grid xs={6}>
                            <Controller
                                name="bankCountry"
                                control={control}
                                defaultValue={companyCountryCode}
                                render={({ field }) => <CountrySelect
                                    {...augmentedFieldProps(field)}
                                />}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Controller
                    name="accountHolder"
                    control={control}
                    defaultValue=""
                    render={({ field }) => <TextField
                        {...augmentedFieldProps(field)}
                        fullWidth={true}
                    />}
                />

            </Stack>

            <NextStep/>
        </form>
    </>
}
